'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useRouter } from 'next/navigation'
import { PHONE } from '@/shared/constants'
import styles from './styles.module.css'

interface HeaderProps {
  onServicesClick: () => void
  onProjectClick: () => void
  onConsultationClick: () => void
}

const Header: React.FC<HeaderProps> = ({ onServicesClick, onProjectClick, onConsultationClick }) => {
  const router = useRouter()
  const pathname = usePathname()
  const isHomePage = pathname === '/'
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isServicesExpanded, setIsServicesExpanded] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)

  // Определение десктоп/планшет/мобилка и управление видимостью меню
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      const newIsDesktop = width >= 1440
      const isTablet = width >= 768 && width < 1440
      const isMobile = width < 768
      
      setIsDesktop(newIsDesktop)
      
      // Планшетное/мобильное меню должно быть скрыто на десктопе
      if (newIsDesktop) {
        setIsServicesExpanded(false)
        setIsMenuOpen(false)
      }
      
      // На планшете и мобилке меню может работать
      // Но если мы перешли с десктопа на планшет, состояние уже правильное
    }

    // Проверяем при монтировании
    checkScreenSize()

    // Подписываемся на изменения размера окна
    window.addEventListener('resize', checkScreenSize)

    return () => {
      window.removeEventListener('resize', checkScreenSize)
    }
  }, [])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const toggleServices = () => {
    setIsServicesExpanded(!isServicesExpanded)
  }

  const navigateAndCloseMenu = (path: string) => {
    router.push(path)
    setIsServicesExpanded(false)
    setIsMenuOpen(false)
  }

  const handleTechnicalSupportClick = () => {
    navigateAndCloseMenu('/services/support')
  }

  const handleTechnologyExpertiseClick = () => {
    onConsultationClick()
  }

  const handleComplexSolutionsClick = () => {
    navigateAndCloseMenu('/services/complex-solutions')
  }

  const handleOutstaffingClick = () => {
    navigateAndCloseMenu('/services/outstaffing')
  }

  const handleWebAppsClick = () => {
    navigateAndCloseMenu('/services/web-apps')
  }

  // Блокировка прокрутки при открытом меню
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Очистка при размонтировании
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        {/* Left block - Logo and Navigation */}
        <div className={styles.leftBlock}>
          {/* Logo */}
          <div className={styles.logoContainer}>
            {isHomePage ? (
              <Image src="/logo.svg" alt="true.code" width={120} height={32} className={styles.logo} priority />
            ) : (
              <Link href="/">
                <Image src="/logo.svg" alt="true.code" width={120} height={32} className={styles.logo} priority />
              </Link>
            )}
          </div>
          
          {/* Navigation */}
          <nav className={styles.nav}>
            <div className={styles.navItem} onClick={onServicesClick}>
              <span className={styles.navItemText}>Услуги</span>
              <svg className={styles.navItemIcon} viewBox="0 0 16 16" fill="none">
                <path d="M11.06 5.53L8 8.58333L4.94 5.53L4 6.47L8 10.47L12 6.47L11.06 5.53Z" fill="white" />
              </svg>
            </div>
            <Link href="/it-solutions/" className={styles.navItem}>
              <span className={styles.navItemText}>ИТ-продукты</span>
            </Link>
            {/* <div className={styles.navItem}>
              <span className={styles.navItemText}>О нас</span>
            </div> */}
            <Link href="/cases" className={styles.navItem}>
              <span className={styles.navItemText}>Кейсы</span>
            </Link>
            <Link href="/articles" className={styles.navItem}>
              <span className={styles.navItemText}>Блог</span>
            </Link>
            <Link href="/contacts" className={styles.navItem}>
              <span className={styles.navItemText}>Контакты</span>
            </Link>
          </nav>
        </div>
        
        {/* Right block - Phone, Button and Burger */}
        <div className={styles.rightBlock}>
          <Link href={`tel:${PHONE}`} className={styles.phone}>
            <span className={styles.phoneText}>+7(395)226–12–60</span>
          </Link>
          <div className={styles.ctaGroup}>
            <button
              className={styles.button}
              type="button"
              onClick={onProjectClick}
            >
              <span className={styles.buttonText}>Обсудить проект</span>
              <svg className={styles.buttonIcon} viewBox="0 0 16 16" fill="none">
                <path d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z" fill="white" />
              </svg>
            </button>
            <a
              href="https://support.t-code.ru/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.supportLink}
              aria-label="support.t-code.ru"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden>
                <path
                  d="M7.99984 8C9.47317 8 10.6665 6.80666 10.6665 5.33333C10.6665 3.86 9.47317 2.66666 7.99984 2.66666C6.5265 2.66666 5.33317 3.86 5.33317 5.33333C5.33317 6.80666 6.5265 8 7.99984 8ZM7.99984 9.33333C6.21984 9.33333 2.6665 10.2267 2.6665 12V13.3333H13.3332V12C13.3332 10.2267 9.77984 9.33333 7.99984 9.33333Z"
                  fill="white"
                />
              </svg>
            </a>
          </div>
          {!isDesktop && (isMenuOpen ? (
            <button className={styles.burgerButton} onClick={toggleMenu}>
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 12C0 5.37258 5.37258 0 12 0H28C34.6274 0 40 5.37258 40 12V28C40 34.6274 34.6274 40 28 40H12C5.37258 40 0 34.6274 0 28V12Z" fill="#2F2F2F"/>
                <path d="M24.6663 16.2734L23.7263 15.3334L19.9997 19.06L16.273 15.3334L15.333 16.2734L19.0597 20L15.333 23.7267L16.273 24.6667L19.9997 20.94L23.7263 24.6667L24.6663 23.7267L20.9397 20L24.6663 16.2734Z" fill="white"/>
              </svg>
            </button>
          ) : (
            <button className={styles.burgerButton} onClick={toggleMenu}>
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 12C0 5.37258 5.37258 0 12 0H28C34.6274 0 40 5.37258 40 12V28C40 34.6274 34.6274 40 28 40H12C5.37258 40 0 34.6274 0 28V12Z" fill="#2F2F2F"/>
                <path d="M14 24H26V22.6667H14V24ZM14 20.6667H26V19.3333H14V20.6667ZM14 16V17.3333H26V16H14Z" fill="white"/>
              </svg>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Menu Overlay - только на планшете и мобилке */}
      {isMenuOpen && !isDesktop && (
        <div className={styles.menuOverlay}>
          <div className={styles.menuContent}>
            {/* Menu items */}
            <div className={styles.menuItems}>
            <div className={styles.menuSection}>
                <Link
                  href="https://support.t-code.ru/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.menuItem}
                  onClick={toggleMenu}
                >
                  <span className={styles.menuItemText}>Вход в Личный кабинет</span>
                </Link>
              </div>
              {/* Services section */}
              <div className={styles.menuSection}>
                <div className={styles.menuItem} onClick={toggleServices}>
                  <span className={styles.menuItemText}>Услуги</span>
                  <svg className={`${styles.menuItemIcon} ${isServicesExpanded ? styles.expanded : ''}`} viewBox="0 0 16 16" fill="none">
                    <path d="M11.06 5.53L8 8.58333L4.94 5.53L4 6.47L8 10.47L12 6.47L11.06 5.53Z" fill="white" />
                  </svg>
                </div>
                
                {isServicesExpanded && (
                  <div className={styles.subMenu}>
                    <div className={styles.subMenuHeader}>
                      <span className={styles.subMenuHeaderText}>Заказная разработка</span>
                    </div>
                    <div className={styles.subMenuGroup}>
                      <div className={styles.subMenuItem} onClick={handleWebAppsClick}>
                        <span className={styles.subMenuItemText}>Сайты и приложения</span>
                      </div>
                      <Link href="/services/integrations" className={styles.subMenuItem} onClick={toggleMenu}>
                        <span className={styles.subMenuItemText}>Интеграции</span>
                      </Link>
                      <div className={styles.subMenuItem} onClick={handleComplexSolutionsClick}>
                        <span className={styles.subMenuItemText}>Комплексные решения</span>
                      </div>
                    </div>
                    <div className={styles.subMenuHeader} onClick={handleTechnicalSupportClick}>
                      <span className={styles.subMenuHeaderText}>Техническое сопровождение</span>
                    </div>
                    <div className={styles.subMenuHeader} onClick={handleTechnologyExpertiseClick}>
                      <span className={styles.subMenuHeaderText}>Технологическая экспертиза</span>
                    </div>
                    <div className={styles.subMenuHeader} onClick={handleOutstaffingClick}>
                      <span className={styles.subMenuHeaderText}>Аутстаффинг</span>
                    </div>
                    <Link href="/services/" className={`${styles.subMenuHeader} ${styles.allServicesLink}`} onClick={toggleMenu}>
                      <span className={styles.allServicesLinkText}>Смотреть все услуги</span>
                      <svg className={styles.allServicesLinkIcon} width={10} height={10} viewBox="0 0 16 16" fill="none" aria-hidden>
                        <path
                          d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z"
                          fill="currentColor"
                        />
                      </svg>
                    </Link>
                  </div>
                )}
              </div>

              {/* IT Solutions */}
              <div className={styles.menuSection}>
                <Link href="/it-solutions" className={styles.menuItem} onClick={toggleMenu}>
                  <span className={styles.menuItemText}>ИТ-продукты</span>
                </Link>
              </div>

              {/* About */}
              {/* <div className={styles.menuSection}>
                <div className={styles.menuItem} onClick={onConsultationClick}>
                  <span className={styles.menuItemText}>О нас</span>
                </div>
              </div> */}

              {/* Cases */}
              <div className={styles.menuSection}>
                <Link href="/cases" className={styles.menuItem} onClick={toggleMenu}>
                  <span className={styles.menuItemText}>Кейсы</span>
                </Link>
              </div>

              {/* Blog */}
              <div className={styles.menuSection}>
                <Link href="/articles" className={styles.menuItem} onClick={toggleMenu}>
                  <span className={styles.menuItemText}>Блог</span>
                </Link>
              </div>

              {/* Contacts */}
              <div className={styles.menuSection}>
                <Link href="/contacts" className={styles.menuItem} onClick={toggleMenu}>
                  <span className={styles.menuItemText}>Контакты</span>
                </Link>
              </div>
            </div>
            
            {/* Bottom section - Phone and Button */}
            <div className={styles.mobileBottomSection}>
              <Link href={`tel:${PHONE}`} className={styles.mobilePhone}>
                <span className={styles.mobilePhoneText}>+7(395)226–12–60</span>
              </Link>
              <button 
                className={styles.mobileButton} 
                onClick={onProjectClick}
              >
                <span className={styles.mobileButtonText}>Обсудить проект</span>
                <svg className={styles.mobileButtonIcon} viewBox="0 0 16 16" fill="none">
                  <path d="M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z" fill="white" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

Header.displayName = 'Header'

export default Header
